import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import * as _ from "lodash";
import PropTypes from "prop-types";
import { useCallback, useRef, useState } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import AtriusLogo from "@/assets/atrius-horizontal.svg";
import { Slider, Switch } from "@/components";

import CustomNavDropdown from "../../CustomNavDropdown";
import BottomMenu from "./bottom-menu";
import HealthyPanel from "./healthy-panel";

dayjs.extend(relativeTime);

const TopBarViewMode = ({
    options,
    setOptions,
    visibility,
    toggleSettingsVisibility,
    lights,
    mapSettings,
    runDuplicates,
    lastRunDuplicate,
    setAdvancedOperationsVisibility,
}) => {
    const dropdownRef = useRef(null);
    const [timeStamp, setTimeStamp] = useState("");

    let siteName = "";
    let floorSpaceName = "";

    const onOptionChanged = useCallback(
        (title, value) => {
            const newOptions = {
                ...options,
                [_.camelCase(title)]: value,
            };
            setOptions(newOptions);
        },
        [options, setOptions],
    );

    const reloadTimeStamp = useCallback(() => {
        setTimeStamp(dayjs(lastRunDuplicate).fromNow());
    }, [lastRunDuplicate]);

    const toggleDropdown = useCallback(() => {
        dropdownRef.current.dropdownRef.toggle();
    }, []);

    const renderHeader = () => {
        if (
            !_.isEmpty(mapSettings) &&
            mapSettings.floorSpaceInfo &&
            mapSettings.floorSpaceInfo.siteName &&
            mapSettings.floorSpaceInfo.floorSpaceName &&
            mapSettings.floorSpaceInfo.siteName.length > 0 &&
            mapSettings.floorSpaceInfo.floorSpaceName.length > 0
        ) {
            // eslint-disable-next-line react-hooks/immutability
            siteName = mapSettings.floorSpaceInfo.siteName;
            floorSpaceName = mapSettings.floorSpaceInfo.floorSpaceName;
        }
        return (
            <div className="wrapperTopOptions">
                <span className="navbar-brand">
                    <img src={AtriusLogo} width="20" height="24" />
                </span>
                <div className="site-details">
                    <a href="#" className="navbar-brand site-name">
                        {siteName}
                    </a>
                    <span className="top-bar-floorspace-name">
                        {floorSpaceName}
                    </span>
                </div>
            </div>
        );
    };

    const renderOptionSwitch = ({ id, title, checked, disabled }) => (
        <div key={id} className="option">
            <span>{title}</span>
            <Switch
                onChange={(value) => onOptionChanged(id, value)}
                style={{ marginLeft: 5 }}
                checked={checked}
                disabled={disabled}
            />
        </div>
    );

    const renderMoreOptionsButton = () => (
        <span>
            <i className="fas fa-ellipsis-h moreLabel" />
            <span className="moreLabel" style={{ marginLeft: 10 }}>
                More
            </span>
        </span>
    );

    const renderOptions = () => {
        const optionsList = [
            {
                id: "autoHideTopBar",
                title: "Auto-hide Top Bar",
                checked: options.autoHideTopBar,
            },
            {
                id: "showLastBleId",
                title: "Show Last 4 of BLE ID",
                checked: options.showLastBleId,
            },
            {
                id: "gridVisible",
                title: "Grid Visible",
                checked: options.gridVisible,
            },
            { id: "snapping", title: "Snapping", checked: options.snapping },
            {
                id: "apertureVisible",
                title: "Aperture Visible",
                checked: options.apertureVisible,
            },
            {
                id: "backgroundVisible",
                title: "Background Visible",
                checked: options.backgroundVisible,
            },
        ];
        const bottomOptions = [
            {
                label: "Advanced (Bulk) Operations",
                icon: "tools",
                click: () => setAdvancedOperationsVisibility(true),
            },
            {
                label: "Map Settings",
                icon: "cog",
                click: () => toggleSettingsVisibility(true),
            },
        ];

        const { lightOpacity } = options;
        return (
            <CustomNavDropdown
                eventKey={3}
                ref={dropdownRef}
                title={renderMoreOptionsButton()}
                className="more-option"
            >
                {optionsList.map(renderOptionSwitch)}
                <div key="lightOpacity" className="light-opacity option">
                    <span>Light Opacity</span>
                    <Slider
                        enableDots
                        step={25}
                        defaultValue={lightOpacity}
                        onChange={(value) => {
                            onOptionChanged("lightOpacity", value);
                        }}
                    />
                </div>
                <BottomMenu>
                    {bottomOptions.map((bottomOption) => (
                        <BottomMenu.Item
                            key={bottomOption.label}
                            icon={bottomOption.icon}
                            onClick={() => {
                                toggleDropdown();
                                bottomOption.click();
                            }}
                        >
                            {bottomOption.label}
                        </BottomMenu.Item>
                    ))}
                </BottomMenu>
            </CustomNavDropdown>
        );
    };

    return (
        <div className={`lights-map-top-bar ${visibility ? "opened" : ""}`}>
            <nav className="navbar navbar-light navbar-expand-sm">
                {renderHeader()}
                <ul className="navbar-nav ms-auto navBarNav">
                    <span
                        className="topBarButtonHeart"
                        onClick={() => reloadTimeStamp()}
                    >
                        <HealthyPanel
                            lights={lights}
                            mapSettings={mapSettings}
                            duplicateCheck={runDuplicates}
                            timeStamp={timeStamp}
                        />
                    </span>
                    <span className="topBarButtonMore">{renderOptions()}</span>
                </ul>
            </nav>
        </div>
    );
};

TopBarViewMode.propTypes = {
    options: PropTypes.object,
    setOptions: PropTypes.func.isRequired,
    visibility: PropTypes.bool.isRequired,
    toggleSettingsVisibility: PropTypes.func.isRequired,
    lights: PropTypes.array,
    mapSettings: PropTypes.object,
    runDuplicates: PropTypes.func.isRequired,
    lastRunDuplicate: PropTypes.any,
    setAdvancedOperationsVisibility: PropTypes.func.isRequired,
};

const mapStateToProps = ({ topBar, map }) => ({
    visibility: topBar.visibility,
    options: topBar.options,
    lights: _.values(map.values.global.present.lights),
    mapSettings: map.general.settings,
    lastRunDuplicate: topBar.lastRunDuplicate,
});
const mapDispatchToProps = {
    setAdvancedOperationsVisibility:
        actions.topBar.setAdvancedOperationsVisibility,
    setOptions: actions.topBar.setOptions,
    toggleSettingsVisibility: actions.map.general.toggleSettingsVisibility,
    runDuplicates: actions.map.general.runDuplicates,
};
export default connect(mapStateToProps, mapDispatchToProps)(TopBarViewMode);
